/**
 * Ringpuffer der letzten Netzwerkaufrufe (max. 40, davon fürs Melde-Werkzeug
 * die letzten 25 relevant). Patcht `window.fetch` EINMAL beim App-Start
 * (`installNetworkCapture()` in main.tsx) und schneidet je Aufruf Methode,
 * Pfad+Query, Status und Dauer mit.
 *
 * Datensparsamkeit ist Absicht: KEINE Bodies, KEINE Header — dort lägen
 * Tokens (Authorization, X-Tenant-Id). Der `apiClient` loggt Details separat
 * über den `logger`-Ringpuffer, der eigene Schwärzungsregeln hat.
 */
export interface NetworkEntry {
  method: string;
  url: string;
  status: number;
  ok: boolean;
  durationMs: number;
  ts: string;
}

const MAX = 40;
const buffer: NetworkEntry[] = [];
let installed = false;

function record(entry: NetworkEntry): void {
  buffer.push(entry);
  if (buffer.length > MAX) buffer.shift();
}

/** Query-String bleibt erhalten (intern, für Reproduktion wertvoll — z.B. ?id=null), Origin wird abgeschnitten. */
function toPath(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + (u.search || '');
  } catch {
    return url;
  }
}

export function installNetworkCapture(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const start = performance.now();
    const method = (
      init?.method
      ?? (typeof input === 'object' && input !== null && 'method' in input ? (input as Request).method : undefined)
      ?? 'GET'
    ).toUpperCase();
    const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    try {
      const res = await original(input, init);
      record({
        method,
        url: toPath(rawUrl),
        status: res.status,
        ok: res.ok,
        durationMs: Math.round(performance.now() - start),
        ts: new Date().toISOString(),
      });
      return res;
    } catch (err) {
      record({
        method,
        url: toPath(rawUrl),
        status: 0,
        ok: false,
        durationMs: Math.round(performance.now() - start),
        ts: new Date().toISOString(),
      });
      throw err;
    }
  };
}

export function getRecentRequests(n = 25): NetworkEntry[] {
  return buffer.slice(-n);
}

/** Test-Helfer: setzt das `installed`-Flag und den Puffer zurück. */
export function __resetNetworkCaptureForTests(): void {
  installed = false;
  buffer.length = 0;
}
